// useUpLoading.js                  стан завантаження файлів

import { useState } from 'react';
import PropTypes from 'prop-types';

export default function useUpLoading({ path, setPath, folderId, driveApi, setError, serv }) {

    const [destFolder, setDestFolder] = useState('root'); // цільова папка
    const [showFolderPicker, setShowFolderPicker] = useState(false);
    const [folders, setFolders] = useState([]); // поточний рівень
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState([]);
    const [overall, setOverall] = useState({ done: 0, total: 0, bytesSent: 0, bytesTotal: 0 });
    const [fileProgress, setFileProgress] = useState({}); // {key: {loaded,total,percent,status}}


    async function fetchFolders(parent = 'root') {
        const r = await fetch(`/drive.php?action=folders&parent=${encodeURIComponent(parent === 'my-drive' ? 'root' : parent)}`, { credentials: 'include' });
        const j = await serv.safeJson(r);
        if (!r.ok) throw new Error(j?.error || 'folders failed');
        setFolders(j.folders || []);
    }

    function openFolderPicker() {
        setShowFolderPicker(true);
        fetchFolders(path[path.length - 1].id).catch(() => { });
    }

    function enterFolder(f) {
        setPath([...path, { id: f.id, name: f.name }]);
        fetchFolders(f.id).catch(() => { });
    }

    function goUp() {
        if (path.length <= 1) return;
        const s = path.slice(0, -1);
        setPath(s);
        fetchFolders(s[s.length - 1].id).catch(() => { });
    }

    async function createFolder(name) {
        const fd = new FormData();
        const idParent = path[path.length - 1].id === 'my-drive' ? 'root' : path[path.length - 1].id;

        fd.append('parent', idParent);
        fd.append('name', name);
        const r = await fetch('/drive.php?action=mkdir', { method: 'POST', body: fd, credentials: 'include' });
        const j = await serv.safeJson(r);
        if (!r.ok) throw new Error(j?.error || 'mkdir failed');
        await fetchFolders(idParent);
    }

    function xhrUploadSingle({ file, parentId }) {
        return new Promise((resolve, reject) => {
            const rel = file.webkitRelativePath && file.webkitRelativePath.length > 0 ? file.webkitRelativePath : file.name;

            const form = new FormData();
            form.append('parent', parentId);
            form.append('files[]', file, file.name); // не передаємо слеші сюди — PHP їх зріже
            form.append('paths[]', rel);             // кладемо реальний відносний шлях окремо

            const xhr = new XMLHttpRequest();
            xhr.open('POST', '/drive.php?action=upload', true);
            xhr.withCredentials = true;

            const key = rel;

            xhr.upload.onprogress = (e) => {
                setFileProgress((prev) => {
                    const next = { ...prev };
                    next[key] = {
                        loaded: e.loaded,
                        total: e.total || file.size,
                        percent: Math.round(((e.loaded) / (e.total || file.size)) * 100),
                        status: 'uploading',
                    };
                    return next;
                });
            };

            xhr.onreadystatechange = () => {
                if (xhr.readyState !== 4) return;
                try {
                    const ok = xhr.status >= 200 && xhr.status < 300;
                    const resp = JSON.parse(xhr.responseText || '{}');

                    setFileProgress((prev) => {
                        const next = { ...prev };
                        next[key] = {
                            ...(next[key] || { loaded: file.size, total: file.size }),
                            percent: 100,
                            status: ok ? 'done' : 'error',
                        };
                        return next;
                    });

                    if (!ok) {
                        reject(new Error(resp?.error || 'upload failed'));
                        return;
                    }
                    // бек повертає {results:[...]} навіть якщо відправили 1 файл
                    const item = Array.isArray(resp?.results) ? resp.results[0] : resp;
                    resolve(item);
                } catch (e) {
                    reject(new Error('bad json response'));
                }
            };

            xhr.onerror = () => reject(new Error('network error'));
            xhr.send(form);
        });
    }

    /** Конвеєр із конкаренсією */
    async function uploadWithConcurrency(files, parentId, concurrency = 4) {
        const queue = Array.from(files);
        const totalBytes = queue.reduce((s, f) => s + (f.size || 0), 0);

        setOverall({ done: 0, total: queue.length, bytesSent: 0, bytesTotal: totalBytes });
        setFileProgress({});
        setUploadResults([]);
        setError('');

        let inFlight = 0;
        let index = 0;
        const results = [];
        let sentAcc = 0;

        return new Promise((resolve) => {
            const pump = () => {
                if (index >= queue.length && inFlight === 0) {
                    setUploadResults(results);
                    return resolve(results);
                }
                while (inFlight < concurrency && index < queue.length) {
                    const file = queue[index++];
                    inFlight++;

                    xhrUploadSingle({ file, parentId })
                        .then((item) => {
                            results.push(item);
                        })
                        .catch((e) => {
                            results.push({ name: file.name, ok: false, error: e.message || 'upload failed' });
                        })
                        .finally(() => {
                            // підсумок
                            sentAcc += file.size || 0;
                            setOverall((prev) => ({
                                done: prev.done + 1,
                                total: prev.total,
                                bytesSent: sentAcc,
                                bytesTotal: prev.bytesTotal,
                            }));
                            inFlight--;
                            pump();
                        });
                }
            };
            pump();
        });
    }

    async function pickFolderAdvanced() {
        if (!('showDirectoryPicker' in window)) return; // фолбек лишається – звичайний вибір папки

        const root = await window.showDirectoryPicker({ mode: 'read' });

        const files = [];
        const dirs = new Set(); // збережемо ВСІ теки, в т.ч. порожні

        async function walkDir(dirHandle, prefix) {
            // зареєструємо саму теку (порожню теж)
            if (prefix) dirs.add(prefix);
            for await (const [name, entry] of dirHandle.entries()) {
                if (entry.kind === 'directory') {
                    await walkDir(entry, prefix ? `${prefix}/${name}` : entry.name);
                } else {
                    const file = await entry.getFile();
                    // підкинемо webkitRelativePath, щоб бек знав, куди класти
                    const rel = prefix ? `${prefix}/${name}` : name;
                    Object.defineProperty(file, 'webkitRelativePath', { value: rel });
                    files.push(file);
                }
            }
        }
        // старт: включаємо ім’я кореневої теки у шлях
        await walkDir(root, root.name);

        // Відправляємо так само, як твої файли, але додамо dirs[]
        await uploadFilesWithDirs(files, Array.from(dirs));
    }

    async function uploadFilesWithDirs(fileList = [], dirList = []) {
        if ((!fileList || fileList.length === 0) && (!dirList || dirList.length === 0)) return;


        // console.log(dirList);
        // console.log(fileList);
        // if (fileList || dirList) return;


        setUploading(true);
        setUploadResults([]);
        setError('');
        try {
            const parentId = path[path.length - 1].id;

            // 1) створюємо теки наперед (включно з порожніми)
            if (Array.isArray(dirList) && dirList.length > 0) {
                await createDirectoriesBatch(dirList, parentId);
            }

            // 2) файли шлемо тим самим механізмом, що й uploadFiles
            if (Array.from(fileList).length > 0) {
                await uploadWithConcurrency(fileList, parentId, 4);

                // оновлюємо список, якщо ми у тій самій папці
                if (folderId === parentId) {
                    driveApi.loadList(parentId);
                }
            }
        } catch (e) {
            setError(e.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    }

    async function createDirectoriesBatch(dirList, parentId) {
        // готуємо форму з лише dirs[] — бекенд створить їх через ensureDrivePath
        const form = new FormData();
        form.append('parent', parentId);
        for (const d of dirList) {
            // нормалізуємо слеші/крапки, бек і так ще раз підчистить
            const rel = String(d).replace(/\\+/g, '/').replace(/^\.\/+/, '').replace(/\/+$/, '');
            if (rel) form.append('dirs[]', rel);
        }

        // якщо після нормалізації нічого не лишилось — нічого не викликаємо
        if (![...form.keys()].includes('dirs[]')) return;

        const r = await fetch('/drive.php?action=upload', {
            method: 'POST',
            body: form,
            credentials: 'include',
        });
        const j = await serv.safeJson(r);
        if (!r.ok) {
            // не валимо процес повністю — кидаємо помилку вгору, її перехопить uploadFilesWithDirs
            throw new Error(j?.error || 'create directories failed');
        }
        // бек може повернути мінімальний JSON — нічого більше не потрібно
    }

    async function uploadFiles(fileList) {
        if (!fileList || fileList.length === 0) return;

        // console.log(fileList);
        // if (fileList) return;

        setUploading(true);
        try {
            const parentId = path[path.length - 1].id;

            // головне: filename буде relpath (див. xhrUploadSingle)
            await uploadWithConcurrency(fileList, parentId, 4);

            // перезавантажити список, якщо зараз у цій самій папці
            if (folderId === parentId) {
                driveApi.loadList(parentId);
            }
        } catch (e) {
            setError(e.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    }

    // універсальний помічник: виконує tasks з лімітом одночасних запусків
    async function mapWithConcurrency(items, limit, worker) {
        const results = new Array(items.length);
        let next = 0;
        let active = 0;

        return new Promise((resolve) => {
            const runNext = () => {
                if (next >= items.length && active === 0) return resolve(results);
                while (active < limit && next < items.length) {
                    const i = next++;
                    active++;
                    Promise.resolve(worker(items[i], i))
                        .then(res => { results[i] = res; })
                        .catch(err => { results[i] = { ok: false, error: err?.message || String(err) }; })
                        .finally(() => { active--; runNext(); });
                }
            };
            runNext();
        });
    }

    async function deleteFile(selectedList, { concurrency = 4, onProgress } = {}) {
        if (!selectedList?.length) return;
        setUploading(true);
        try {
            let done = 0;

            const results = await mapWithConcurrency(selectedList, concurrency, async ({ id }) => {
                const fd = new FormData();
                fd.append('id', id);

                const r = await fetch('/drive.php?action=trash', {
                    method: 'POST',
                    body: fd,
                    credentials: 'include',
                });

                const j = await serv.safeJson(r);
                done++;
                onProgress?.(done, selectedList.length, { id, ok: r.ok, j });

                return { id, ok: r.ok, j };
            });

            const failed = results.filter(x => !x.ok);
            if (failed.length) {
                const msg = failed.slice(0, 8) // обрізаємо довгий список
                    .map(x => `${x.id}: ${x.j?.error || 'delete failed'}`)
                    .join('\n');
                alert(`Не вдалося видалити ${failed.length} елемент(и):\n${msg}`);
            }
            await driveApi.loadList(folderId);
        } finally {
            setUploading(false);
        }
    }


    async function moveFile(selectedList, newParent = '', oldParent = folderId, { concurrency = 4, onProgress } = {}) {
        if (!selectedList?.length) return;
        setUploading(true);
        try {
            let done = 0;
            const results = await mapWithConcurrency(selectedList, concurrency, async ({ id }) => {
                const fd = new FormData();
                fd.append('id', id);
                fd.append('parent', newParent);
                if (oldParent) fd.append('oldParent', oldParent);
                const r = await fetch('/drive.php?action=move', {
                    method: 'POST',
                    body: fd,
                    credentials: 'include'
                });
                const j = await serv.safeJson(r);
                if (!r.ok) { alert('Помилка: ' + (j.error || 'move failed')); return; }
                done++;
                onProgress?.(done, selectedList.length, { id, ok: r.ok, j });

                return { id, ok: r.ok, j };
            });
            const failed = results.filter(x => !x.ok);
            if (failed.length) {
                const msg = failed.slice(0, 8) // обрізаємо довгий список
                    .map(x => `${x.id}: ${x.j?.error || 'move failed'}`)
                    .join('\n');
                alert(`Не вдалося перемістити ${failed.length} елемент(и):\n${msg}`);
            }
            await driveApi.loadList(folderId);
        } finally {
            setUploading(false);
        }
    }

    async function renameItem(id, newName) {
        const form = new FormData();
        form.append('id', id);
        form.append('name', newName);
        const r = await fetch('/drive.php?action=rename', { method: 'POST', body: form, credentials: 'include' });
        const j = await serv.safeJson(r);
        if (!r.ok || j?.ok === false) throw new Error(j?.error || 'rename failed');
        return j.file || j;
    }

    // пакетне
    async function renameBatch(pairs /* [{id, name}] */) {
        const form = new FormData();
        pairs.forEach(p => { form.append('ids[]', p.id); form.append('names[]', p.name); });
        const r = await fetch('/drive.php?action=rename', { method: 'POST', body: form, credentials: 'include' });
        const j = await serv.safeJson(r);
        if (!r.ok) throw new Error(j?.error || 'rename failed');
        return j.results || [];
    }


    return {
        destFolder, setDestFolder, showFolderPicker, setShowFolderPicker, folders, setFolders, path, setPath, uploading, setUploading, uploadResults, setUploadResults,
        openFolderPicker, enterFolder, goUp, /*chooseThisFolder,*/ createFolder, uploadFiles, deleteFile, moveFile, overall, fileProgress, pickFolderAdvanced,
        renameItem, renameBatch,
    };
}

useUpLoading.propTypes = {
    path: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    })).isRequired,
    setPath: PropTypes.func.isRequired,
    folderId: PropTypes.string,
    driveApi: PropTypes.object.isRequired,
    setError: PropTypes.func.isRequired,
};
